"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { cn } from "@v1/ui/utils";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribeVisualViewportCssVars } from "../main-workspace-layout/use-visual-viewport-height";
import { type MessengerContextValue, MessengerProvider } from "./messenger-context";
import {
  MESSENGER_PARAM,
  type MessengerSurface,
  type MessengerView,
  isWideView,
  parseMessengerParam,
} from "./messenger-types";
import { MessengerWindow } from "./messenger-window";
import { useMessengerNav } from "./use-messenger-nav";

/** `?help=open`: open like a click on the launcher (unread, open chat or new chat). */
export const MESSENGER_PARAM_OPEN = "open";

type Landing = FunctionReturnType<typeof api.customerChat.messenger_lists.getMessengerLanding>;

function viewOfLanding(landing: Landing | undefined): MessengerView {
  if (landing === undefined) return { kind: "home" };
  if (landing.kind === "chat") return { kind: "chat", sessionId: landing.session_id };
  if (landing.kind === "ticket") return { kind: "ticket", taskId: landing.task_id };
  return { kind: "chat", sessionId: null };
}

/** Phones: page behind must not scroll while the full-screen window covers it. */
function usePhoneScrollLock(): void {
  useEffect(() => {
    if (!window.matchMedia("(max-width: 639.98px)").matches) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = previous;
    };
  }, []);
}

/**
 * Intercom-style window. `top` hangs under the app's top bar, `bottom` sits
 * above the bubble; help views get wide. Phones: full screen.
 */
export function MessengerPopover({
  value,
  placement,
  large = false,
}: {
  readonly value: MessengerContextValue;
  readonly placement: "top" | "bottom";
  /** „KI fragen“: wide from the start, under the help header instead of over it. */
  readonly large?: boolean;
}): ReactElement {
  const t = useTranslations("messenger.tabs");
  const wide = large || isWideView(value.nav.view);
  const { close } = value;
  useEffect(() => subscribeVisualViewportCssVars(), []);
  usePhoneScrollLock();
  useEffect(() => {
    if (close === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);
  return (
    <MessengerProvider value={value}>
      <div
        role="dialog"
        aria-label={t("hilfe")}
        className={cn(
          // Phones: pinned to the visual viewport — iOS keyboard otherwise pushes header off, page shows below.
          "fixed inset-x-0 top-[var(--app-visual-viewport-offset-top,0px)] z-[1050] flex h-[var(--app-visual-viewport-height,100dvh)] flex-col overflow-hidden bg-background print:hidden",
          "sm:inset-auto sm:rounded-2xl sm:shadow-[0_12px_48px_rgba(0,0,0,0.18)] sm:ring-1 sm:ring-black/[0.06]",
          "animate-in fade-in-0 zoom-in-95 duration-200 sm:transition-[width,height] sm:duration-300 sm:ease-out",
          placement === "top"
            ? "origin-top-right slide-in-from-top-2 sm:right-3 sm:top-[3.25rem]"
            : "origin-bottom-right slide-in-from-bottom-2 sm:bottom-[5.5rem] sm:right-5",
          placement === "top"
            ? wide
              ? "sm:h-[calc(100dvh-4.5rem)] sm:w-[min(820px,calc(100vw-1.5rem))]"
              : "sm:h-[min(720px,calc(100dvh-4.5rem))] sm:w-[400px]"
            : large
              ? "sm:top-[4.75rem] sm:h-auto sm:w-[min(820px,calc(100vw-2.5rem))]"
              : wide
                ? "sm:h-[calc(100dvh-7.5rem)] sm:w-[min(820px,calc(100vw-2.5rem))]"
                : "sm:h-[min(720px,calc(100dvh-7.5rem))] sm:w-[400px]",
        )}
      >
        <MessengerWindow />
      </div>
    </MessengerProvider>
  );
}

export interface MemberMessenger {
  /** Null while loading or when this person has no messenger. */
  readonly value: MessengerContextValue | null;
  readonly open: boolean;
  readonly hasUnread: boolean;
  readonly toggle: () => void;
  readonly openTo: (view: MessengerView) => void;
  readonly openLanding: () => void;
}

/** State of a signed-in person's messenger; opening lands like Intercom. */
export function useMemberMessenger(surface: MessengerSurface): MemberMessenger {
  const config = useQuery(api.customerChat.messenger_lists.getMessengerConfig, { surface });
  const isOn = config?.state === "on";
  const unreadResult = useQuery(api.customerChat.messenger_lists.getMyUnread, isOn ? { surface } : "skip");
  const landing = useQuery(api.customerChat.messenger_lists.getMessengerLanding, isOn ? { surface } : "skip");
  const nav = useMessengerNav();
  const { reset } = nav;
  const [open, setOpen] = useState<boolean>(false);
  const close = useCallback((): void => setOpen(false), []);
  const openTo = useCallback(
    (view: MessengerView): void => {
      reset(view);
      setOpen(true);
    },
    [reset],
  );
  const landingRef = useRef<Landing | undefined>(landing);
  landingRef.current = landing;
  const openLanding = useCallback((): void => openTo(viewOfLanding(landingRef.current)), [openTo]);
  const unread = unreadResult?.items;
  const value = useMemo(
    (): MessengerContextValue | null =>
      config?.state === "on"
        ? { audience: { kind: "member", surface }, config, nav, unread: unread ?? [], close }
        : null,
    [close, config, nav, surface, unread],
  );
  const toggle = useCallback((): void => {
    if (open) setOpen(false);
    else openLanding();
  }, [open, openLanding]);
  return { value, open, hasUnread: (unread?.length ?? 0) > 0, toggle, openTo, openLanding };
}

/** Opens on `?help=…` (mail links, phone menu), then drops the param. Wrap in Suspense. */
export function MessengerDeepLink({ messenger }: { readonly messenger: MemberMessenger }): null {
  const enabled = messenger.value !== null;
  const { openTo, openLanding } = messenger;
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const param = searchParams?.get(MESSENGER_PARAM) ?? null;
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (param === null) handled.current = null;
    if (!enabled || param === null || handled.current === param) return;
    handled.current = param;
    if (param === MESSENGER_PARAM_OPEN) openLanding();
    else {
      const view = parseMessengerParam(param);
      if (view !== null) openTo(view);
    }
    const next = new URLSearchParams(searchParams?.toString() ?? "");
    next.delete(MESSENGER_PARAM);
    const query = next.toString();
    // history-replace: the link only opens the messenger once; dropping its param adds no back step.
    router.replace(query ? `${pathname}?${query}` : (pathname ?? "/"), { scroll: false });
  }, [enabled, openLanding, openTo, param, pathname, router, searchParams]);
  return null;
}

/** Lands once on the right view when the window is a page (portal phone tab). */
export function useLandingOnce(surface: MessengerSurface, onLanding: (view: MessengerView) => void): void {
  const landing = useQuery(api.customerChat.messenger_lists.getMessengerLanding, { surface });
  const done = useRef<boolean>(false);
  useEffect(() => {
    if (done.current || landing === undefined) return;
    done.current = true;
    onLanding(viewOfLanding(landing));
  }, [landing, onLanding]);
}
